/**
 * Globe render settings service — localStorage-persisted configuration
 * for the 3D globe's render quality, texture, and visual preset.
 *
 * Dispatches 'globe-settings-changed' CustomEvent on window whenever
 * a setting changes, with detail: { key, value }.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RenderScale = 'auto' | 'eco' | 'sharp' | '4k' | 'insane';
export type TextureChoice = 'topographic' | 'bluemarble';
export type VisualPreset = 'classic' | 'enhanced';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEYS = {
  renderScale: 'sentinel-globe-render-scale',
  texture: 'sentinel-globe-texture',
  preset: 'sentinel-globe-preset',
} as const;

const DEFAULTS: {
  renderScale: RenderScale;
  texture: TextureChoice;
  preset: VisualPreset;
} = {
  renderScale: 'auto',
  texture: 'topographic',
  preset: 'classic',
};

const RENDER_SCALE_VALUES: readonly RenderScale[] = [
  'auto', 'eco', 'sharp', '4k', 'insane',
];

const TEXTURE_VALUES: readonly TextureChoice[] = ['topographic', 'bluemarble'];
const PRESET_VALUES: readonly VisualPreset[] = ['classic', 'enhanced'];

/** Fixed DPR multipliers for each named scale. */
const DPR_MAP: Record<Exclude<RenderScale, 'auto'>, number> = {
  eco: 1,
  sharp: 1.5,
  '4k': 2,
  insane: 3,
};

const TEXTURE_URLS: Record<TextureChoice, string> = {
  topographic: '/textures/earth-topo-bathy.jpg',
  bluemarble: '/textures/earth-blue-marble.jpg',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null && (allowed as readonly string[]).includes(raw)) {
      return raw as T;
    }
  } catch {
    // localStorage may be unavailable (private browsing, SSR, etc.)
  }
  return fallback;
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silently ignore quota / access errors
  }
}

function notify(key: string, value: string): void {
  window.dispatchEvent(
    new CustomEvent('globe-settings-changed', { detail: { key, value } }),
  );
}

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

/** Current render scale preference. */
export function getRenderScale(): RenderScale {
  return read(STORAGE_KEYS.renderScale, RENDER_SCALE_VALUES, DEFAULTS.renderScale);
}

/** Current texture choice. */
export function getTexture(): TextureChoice {
  return read(STORAGE_KEYS.texture, TEXTURE_VALUES, DEFAULTS.texture);
}

/** Current visual preset. */
export function getVisualPreset(): VisualPreset {
  return read(STORAGE_KEYS.preset, PRESET_VALUES, DEFAULTS.preset);
}

// ---------------------------------------------------------------------------
// Setters
// ---------------------------------------------------------------------------

/** Persist render scale and notify listeners. */
export function setRenderScale(scale: RenderScale): void {
  write(STORAGE_KEYS.renderScale, scale);
  notify(STORAGE_KEYS.renderScale, scale);
}

/** Persist texture choice and notify listeners. */
export function setTexture(texture: TextureChoice): void {
  write(STORAGE_KEYS.texture, texture);
  notify(STORAGE_KEYS.texture, texture);
}

/** Persist visual preset and notify listeners. */
export function setVisualPreset(preset: VisualPreset): void {
  write(STORAGE_KEYS.preset, preset);
  notify(STORAGE_KEYS.preset, preset);
}

// ---------------------------------------------------------------------------
// Derived accessors
// ---------------------------------------------------------------------------

/**
 * Resolve the effective device-pixel ratio.
 *
 * - 'auto' — uses the device's native DPR, clamped to [1, 1.5].
 * - Named presets map to fixed multipliers (eco=1, sharp=1.5, 4k=2, insane=3).
 */
export function getEffectiveDPR(): number {
  const scale = getRenderScale();
  if (scale === 'auto') {
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
    return Math.min(Math.max(dpr, 1), 1.5);
  }
  return DPR_MAP[scale];
}

/**
 * Whether pulse and dash animations should be active.
 * Disabled in Eco mode to conserve GPU resources.
 */
export function isAnimationEnabled(): boolean {
  return getRenderScale() !== 'eco';
}

/**
 * Whether the atmosphere / glow effect should be rendered.
 * Disabled in Eco mode to conserve GPU resources.
 */
export function isAtmosphereEnabled(): boolean {
  return getRenderScale() !== 'eco';
}

/** Resolve the current texture choice to a URL path. */
export function getTextureUrl(): string {
  return TEXTURE_URLS[getTexture()];
}
